import { createContext, useContext, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext.jsx';
import { useMaintenance } from '@/context/MaintenanceContext.jsx';
import {
  STAFF_LOGIN_PATH,
  STAFF_RESET_PATH,
} from '../../shared/publicationAccess.js';
import { isMaintenancePrivilegedUser } from '@/lib/maintenanceAccess.js';
import {
  classifyPublicationRoute,
  isSelectivePublicationRouteAllowed,
  PUBLICATION_ROUTE_CLASSES,
} from '@/lib/publicationRoutePolicy.js';
import { PUBLICATION_MODES } from '../../shared/publicationMode.js';

const PublicationContext = createContext({
  isSelective: false,
  effectivePublicationMode: PUBLICATION_MODES.MAINTENANCE,
  isLoading: false,
  staffLoginPath: STAFF_LOGIN_PATH,
  staffResetPath: STAFF_RESET_PATH,
  isPrivilegedUser: false,
  classifyRoute: classifyPublicationRoute,
  isRouteAllowed: () => false,
  isPublicPath: () => false,
  isStaffAccessPath: () => false,
});

export function PublicationProvider({ children }) {
  const { user, isLoading } = useAuth();
  const { effectivePublicationMode, allowedRoles } = useMaintenance();

  const value = useMemo(() => {
    const privileged = isMaintenancePrivilegedUser(user, allowedRoles);
    const isRouteClass = (pathname, routeClass) =>
      classifyPublicationRoute(pathname).routeClass === routeClass;

    return {
      isSelective: effectivePublicationMode === PUBLICATION_MODES.SELECTIVE,
      effectivePublicationMode,
      isLoading,
      staffLoginPath: STAFF_LOGIN_PATH,
      staffResetPath: STAFF_RESET_PATH,
      isPrivilegedUser: privileged,
      classifyRoute: classifyPublicationRoute,
      isRouteAllowed: (pathname) =>
        isSelectivePublicationRouteAllowed(classifyPublicationRoute(pathname)),
      isPublicPath: (pathname) =>
        isRouteClass(pathname, PUBLICATION_ROUTE_CLASSES.PUBLIC),
      isStaffAccessPath: (pathname) =>
        isRouteClass(pathname, PUBLICATION_ROUTE_CLASSES.STAFF_ACCESS),
    };
  }, [allowedRoles, effectivePublicationMode, isLoading, user]);

  return (
    <PublicationContext.Provider value={value}>
      {children}
    </PublicationContext.Provider>
  );
}

export const usePublication = () => useContext(PublicationContext);

export default PublicationContext;
